import React, { useState, useEffect, Suspense, useTransition } from 'react';
import { TraceabilityDashboardApi } from 'api/endpoints/TraceabilityDashboard';
import { Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import RangeDatePicker from 'screens/common/RageDatePicker';
import StatisticsCard from 'components/dashboards/saas/stats-cards/StatisticsCard';
import StatiticsCardsSkeleton from 'components/dashboards/default/skeleton/StatiticsCardsSkeleton';

// Use a single ObservationRenderer with direct import
const ObservationRenderer = React.lazy(() =>
  import('screens/common/ObservationRender')
);

// Error boundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Chart error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="warning">
          Unable to load chart. Please try refreshing.
        </Alert>
      );
    }

    return this.props.children;
  }
}

// Add PropTypes validation for children
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

// Simplified wrapper for ObservationRenderer to handle errors and loading
const ChartWrapper = ({ dataset, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!dataset || isEmpty(dataset)) {
    return null;
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        }
      >
        <ObservationRenderer dataset={dataset} loading={loading} />
      </Suspense>
    </ErrorBoundary>
  );
};

ChartWrapper.propTypes = {
  dataset: PropTypes.object,
  loading: PropTypes.bool
};

function TraceabilityDashboard() {
  // States for all API responses
  const [procurementCardData, setProcurementCardData] = useState({});
  const [transferCardData, setTransferCardData] = useState({});
  const [receptionCardData, setReceptionCardData] = useState({});
  const [pendingTransferCardData, setPendingTransferCardData] = useState({});
  const [processCardData, setProcessCardData] = useState({});
  const [warehousePieChartData, setWarehousePieChartData] = useState([]);
  const [tracebilityCropBarChartData, setTracebilityCropBarChartData] =
    useState([]);
  const [procurementMapData, setProcurementMapData] = useState([]);

  // Loading states for individual API calls
  const [procurementCardLoading, setProcurementCardLoading] = useState(false);
  const [transferCardLoading, setTransferCardLoading] = useState(false);
  const [receptionCardLoading, setReceptionCardLoading] = useState(false);
  const [processCardLoading, setProcessCardLoading] = useState(false);

  const [warehousePieChartLoading, setWarehousePieChartLoading] =
    useState(false);
  const [tracebilityCropBarChartLoading, setTracebilityCropBarChartLoading] =
    useState(false);
  const [procurementMapLoading, setProcurementMapLoading] = useState(false);

  const [error, setError] = useState(null);
  const [showSpinner, setShowSpinner] = useState(true);

  // Date range state - use empty strings like weather dashboard
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Helper function to format date like weather dashboard
  const formatDate = date => {
    if (!date) return '';
    const d = new Date(date);
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Handle date range changes exactly like weather dashboard
  const handleDateChange = (start, end) => {
    if ((start && end) || (start === '' && end === '')) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  // Fetch card data when dates change (like weather dashboard pattern)
  useEffect(() => {
    const formattedStartDate = startDate ? formatDate(startDate) : '';
    const formattedEndDate = endDate ? formatDate(endDate) : '';

    // Procurement Card Data
    setProcurementCardLoading(true);
    TraceabilityDashboardApi.procurementCard({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    })
      .then(response => {
        setProcurementCardData(response.data?.results || {});
      })
      .catch(err => {
        console.error('Error fetching procurement card data:', err);
      })
      .finally(() => {
        setProcurementCardLoading(false);
      });

    // Transfer Card Data
    setTransferCardLoading(true);
    TraceabilityDashboardApi.transferCard({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    })
      .then(response => {
        setTransferCardData(response.data?.results || {});
      })
      .catch(err => {
        console.error('Error fetching transfer card data:', err);
      })
      .finally(() => {
        setTransferCardLoading(false);
      });

    // Reception Card Data
    setReceptionCardLoading(true);
    TraceabilityDashboardApi.receptionCard({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    })
      .then(response => {
        setReceptionCardData(response.data?.results || {});
      })
      .catch(err => {
        console.error('Error fetching reception card data:', err);
      })
      .finally(() => {
        setReceptionCardLoading(false);
      });

    // Pending Transfer Card Data
    setProcessCardLoading(true);
    TraceabilityDashboardApi.processCard({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    })
      .then(response => {
        setProcessCardData(response.data?.results || {});
      })
      .catch(err => {
        console.error('Error fetching pending transfer card data:', err);
      })
      .finally(() => {
        setProcessCardLoading(false);
      });
  }, [startDate, endDate]);

  // Fetch chart data when dates change (like weather dashboard pattern)
  useEffect(() => {
    const formattedStartDate = startDate ? formatDate(startDate) : '';
    const formattedEndDate = endDate ? formatDate(endDate) : '';

    // Warehouse Pie Chart Data
    setWarehousePieChartLoading(true);
    TraceabilityDashboardApi.warehousePieChart({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      limit: 10
    })
      .then(response => {
        setWarehousePieChartData(response.data?.results || []);
      })
      .catch(err => {
        console.error('Error fetching warehouse pie chart data:', err);
      })
      .finally(() => {
        setWarehousePieChartLoading(false);
      });

    // Traceability Crop Bar Chart Data
    setTracebilityCropBarChartLoading(true);
    TraceabilityDashboardApi.tracebilityCropBarChart({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      limit: 10
    })
      .then(response => {
        setTracebilityCropBarChartData(response.data?.results || []);
      })
      .catch(err => {
        console.error('Error fetching traceability crop bar chart data:', err);
      })
      .finally(() => {
        setTracebilityCropBarChartLoading(false);
      });

    // Procurement Map Data
    setProcurementMapLoading(true);
    TraceabilityDashboardApi.procurementMap({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      limit: 10
    })
      .then(response => {
        setProcurementMapData(response.data?.results || []);
      })
      .catch(err => {
        console.error('Error fetching procurement map data:', err);
      })
      .finally(() => {
        setProcurementMapLoading(false);
      });
  }, [startDate, endDate]);

  const cardData = {
    ...procurementCardData,
    ...transferCardData,
    ...receptionCardData,
    ...processCardData
  };

  const statsData = Object.keys(cardData).map((key, index) => {
    const item = cardData[key];

    const stat = {
      sign: item.percentage > 0 ? '+' : '',
      value: item.count,
      info: ``,
      title: item.title,
      suffix: '',
      desc: ''
    };

    return (
      <Col key={key} md={6} lg={3}>
        <StatisticsCard
          stat={stat}
          style={{ minWidth: '12rem', height: '100%' }}
          index={index}
        />
      </Col>
    );
  });

  // Check if any loading is happening (like weather dashboard)
  const isLoading =
    procurementCardLoading ||
    transferCardLoading ||
    receptionCardLoading ||
    processCardLoading ||
    warehousePieChartLoading ||
    tracebilityCropBarChartLoading ||
    procurementMapLoading;

  // Handle spinner timing like weather dashboard
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      {showSpinner ? (
        <StatiticsCardsSkeleton />
      ) : (
        <>
          <Row className="g-3 mb-6 mt-1">
            <Col md="4" className="mt-0">
              <RangeDatePicker onDateChange={handleDateChange} />
            </Col>
          </Row>

          {cardData &&
            Object.values(cardData).map(item => item?.count >= 0) && (
              <Row className="g-3 mb-4">{statsData}</Row>
            )}

          {!isEmpty(warehousePieChartData) && (
            <Row className="g-3 mb-3">
              <ChartWrapper
                dataset={warehousePieChartData}
                loading={warehousePieChartLoading}
              />
            </Row>
          )}

          {!isEmpty(tracebilityCropBarChartData) && (
            <Row className="g-3 mb-3">
              <ChartWrapper
                dataset={tracebilityCropBarChartData}
                loading={tracebilityCropBarChartLoading}
              />
            </Row>
          )}

          {!isEmpty(procurementMapData) && (
            <Row className="g-3 mb-3">
              <ChartWrapper
                dataset={procurementMapData}
                loading={procurementMapLoading}
              />
            </Row>
          )}
        </>
      )}
    </div>
  );
}

export default TraceabilityDashboard;
